import {
  IPagedSalaryPayoutSummary,
  ISalaryPayoutBatchAddRes,
  ISalaryPayoutBatchMonthRes,
  ISalaryPayoutBatchRes,
  ITeacherPayoutMethodRes,
  ITeacherSalaryPayoutPreviewRes,
  ITeacherSalaryPayoutRes,
} from '~/types/salary-payout'
import http from '~/utils/http'

/** 获取某月所有教师薪资 + 支付状态汇总 */
export function listMonthlySalaryPayouts(
  year: number,
  month: number,
  page: number,
  size: number,
) {
  return http.get<IPagedSalaryPayoutSummary>('/admin/teacher/salary-payout', {
    params: { year, month, page, size },
    // First-load on a fresh DB triggers per-teacher salary stat queries
    // (Wise call-out latency + remote DB). 60s tolerance until paged caching
    // / a more granular endpoint exists.
    timeout: 60_000,
  })
}

/** 单个老师本月薪资预览（含 existingPayout） */
export function previewTeacherSalaryPayout(
  teacherId: string | number,
  year: number,
  month: number,
) {
  return http.get<ITeacherSalaryPayoutPreviewRes>(
    `/admin/teacher/${teacherId}/salary-payout/preview`,
    { params: { year, month } },
  )
}

/** 阶段 1：创建 Wise 报价（同时确保 recipient 已建/复用） */
export function createTeacherSalaryPayout(
  teacherId: string | number,
  year: number,
  month: number,
  payload: { sourceCurrency?: string; reference?: string } = {},
) {
  return http.post<ITeacherSalaryPayoutRes>(
    `/admin/teacher/${teacherId}/salary-payout`,
    { year, month, ...payload },
  )
}

/** 阶段 2：把 Wise 报价转成 Transfer */
export function createWiseTransferForSalaryPayout(
  payoutId: string | number,
  reference?: string,
) {
  return http.post<ITeacherSalaryPayoutRes>(
    `/admin/teacher/salary-payout/${payoutId}/transfer`,
    reference ? { reference } : {},
  )
}

/** 阶段 3：出资（默认 BALANCE，可显式指定其他 pay-in 方式） */
export function fundTeacherSalaryPayout(
  payoutId: string | number,
  fundingType?: string,
) {
  return http.post<ITeacherSalaryPayoutRes>(
    `/admin/teacher/salary-payout/${payoutId}/fund`,
    fundingType ? { fundingType } : {},
  )
}

/** 拉取老师提交的收款方式详情（admin 审核前看一眼） */
export function getTeacherPayoutMethodForAdmin(teacherId: string | number) {
  return http.get<ITeacherPayoutMethodRes | null>(
    `/admin/teacher/${teacherId}/payout-method`,
  )
}

/** 老师的历史 payout 列表（按月倒序） */
export function getTeacherPayoutHistory(
  teacherId: string | number,
  limit = 24,
) {
  return http.get<ITeacherSalaryPayoutRes[]>(
    `/admin/teacher/${teacherId}/salary-payout/history`,
    { params: { limit } },
  )
}

/** 验证老师收款方式（管理员审核） */
export function verifyTeacherPayoutMethod(teacherId: string | number) {
  return http.post(`/admin/teacher/${teacherId}/payout-method/verify`)
}

/** 本月的批量转账（含已取消的历史批次）和 Wise 余额 */
export function getSalaryPayoutBatchMonth(year: number, month: number) {
  return http.get<ISalaryPayoutBatchMonthRes>('/admin/teacher/salary-payout-batch', {
    params: { year, month },
    timeout: 30_000,
  })
}

/** 把老师加入本月批次（没有就先建批次）；每位老师要重新报价 + 建转账，人多时会比较久 */
export function addTeachersToSalaryPayoutBatch(
  year: number,
  month: number,
  teacherIds: (string | number)[],
) {
  return http.post<ISalaryPayoutBatchAddRes>(
    '/admin/teacher/salary-payout-batch/teachers',
    { year, month, teacherIds },
    { timeout: 300_000 },
  )
}

/** 锁定批次：之后不能再加人，可以去 Wise 一次付款 */
export function completeSalaryPayoutBatch(batchId: string | number) {
  return http.post<ISalaryPayoutBatchRes>(
    `/admin/teacher/salary-payout-batch/${batchId}/complete`,
    {},
    { timeout: 60_000 },
  )
}

/** 取消整个批次（只能在付款前） */
export function cancelSalaryPayoutBatch(batchId: string | number) {
  return http.post<ISalaryPayoutBatchRes>(
    `/admin/teacher/salary-payout-batch/${batchId}/cancel`,
    {},
    { timeout: 60_000 },
  )
}
